import { Box, Icon, Theme, Typography } from "@outschool/backpack";
import { IsValidTimeRangeForBlackFridaySale } from "@outschool/business-rules";
import { faTimes } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { joinMembershipPath } from "@outschool/routes";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useIsMobile, useNavigation } from "@outschool/ui-utils";
import React, { useEffect } from "react";

import { useBannerDismissal } from "../../hooks/useBannerDismissal";

const BANNER_NAME = "black_friday_sale";

export default function BlackFridaySaleBanner() {
  const { t } = useTranslation(
    "client\\components\\banners\\BlackFridaySaleBanner",
  );
  const navigate = useNavigation();
  const trackEvent = useTrackEvent();
  const isMobile = useIsMobile();
  const { dismissBanner, isBannerDismissed } = useBannerDismissal();
  // Track if banner view has been tracked for this mount to prevent duplicate tracking
  const hasTrackedViewRef = React.useRef<boolean>(false);

  useEffect(() => {
    if (!hasTrackedViewRef.current) {
      hasTrackedViewRef.current = true;
      trackEvent("black_friday_sale_banner_view", {
        is_mobile: isMobile,
      });
    }
  }, [trackEvent, isMobile]);

  const handleBannerClick = () => {
    trackEvent("black_friday_sale_banner_click", {
      is_mobile: isMobile,
    });
    navigate(joinMembershipPath());
  };

  const handleDismiss = (event: React.MouseEvent) => {
    event.stopPropagation();
    trackEvent("black_friday_sale_banner_dismiss", {
      is_mobile: isMobile,
    });
    dismissBanner(BANNER_NAME);
  };

  if (!IsValidTimeRangeForBlackFridaySale() || isBannerDismissed(BANNER_NAME)) {
    return null;
  }

  return (
    <Box
      sx={(theme: Theme) => ({
        display: "flex",
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary[600],
        minHeight: "50px",
        cursor: "pointer",
        padding: "0 1em",
        [theme.breakpoints.down("md")]: {
          padding: "20px",
        },
      })}
      onClick={handleBannerClick}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "1440px",
          margin: "0 auto",
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography variant="body1" sx={{ textAlign: "center" }}>
              <Box component="span" sx={{ fontWeight: "bold" }}>
                {t("Two free months!")}
              </Box>{" "}
              {t("Our BIGGEST sale of the year. Offer ends Dec 1,")}
              <Box
                component="span"
                sx={{
                  textDecoration: "underline",
                  ml: "4px",
                }}
              >
                {t("try now!")}
              </Box>
            </Typography>
          </Box>
        </Box>
        <Icon
          icon={faTimes}
          onClick={handleDismiss}
          aria-label={t("Close banner")}
          sx={(theme: Theme) => ({
            color: theme.palette.common.white,
            position: "absolute",
            right: 0,
            width: "2rem",
            height: "2rem",
            cursor: "pointer",
          })}
        />
      </Box>
    </Box>
  );
}
